import { Box, Button, Checkbox, Typography } from '@mui/material';

import {
  IStyles,
  ImageWrapper,
  InstructionsPanel,
  Loader,
  QuestionContainerWithSolution,
  cloneObject,
  deserify,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
  MultiSubjectSection,
  SubmissionType,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { HomeworkContent } from '@protos/learning_management/lms.hw.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';

import { ViewHwStatCard } from './components/ViewHwStatCard';
import {
  aiHomeworkOpenEvent,
  aiHomeworkViewEvent,
} from '../../../Homework/homework.events';
import { useLmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler } from '@geneo2-web/services-clients';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { canEdit } from '../../../Assessment/Common/ReviewAssessment/functions';
import { buildSortedHomeworkContent } from 'apps/teacher/src/utils/functions';
import IncludeImageButton from '../../QuestionPaperGenerator/ReviewQuestionPaper/Buttons/IncludeImageButton';
import { questionHasImage } from '../../QuestionPaperGenerator/ReviewQuestionPaper/utils';
import { getSubjectNameFromEnum } from '../../../Home/home.utils';
import { useNavigate } from 'react-router-dom';
import { OMR_ACTIVE } from 'apps/teacher/src/routeHandling/RoutesNomenclature';
import { Feature } from '@protos/school_management/school.db_pb';

const styles: IStyles = {
  root: {
    // width: '100%',
    display: 'flex',
    flexDirection: 'column',
    p: {
      xs: `${pxToRem(20)} ${pxToRem(18)} `,
      lg: `${pxTovW(37)} ${pxTovW(240)}`,
    },
    gap: { xs: pxToRem(20), lg: pxTovW(30) },
  },
  mainContainer: {
    display: 'flex',
    // alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: { xs: pxToRem(0), md: pxTovW(20) },
    position: 'relative',
    // backgroundColor: 'red',
  },

  physicalSubmission: {
    width: { xs: '95%', md: '100%' },
    height: { xs: pxToRem(286), lg: pxTovW(455) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(17), md: pxTovW(30) },
    justifyContent: 'center',
    textAlign: 'center',
    background: '#F5FAFF',
    border: '1px solid #93CBFF',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    margin: 'auto',
    marginBottom: { xs: pxToRem(20), md: 'unset' },
  },

  rightPanel: {
    flexBasis: { lg: pxTovW(793) },

    overflow: 'auto',
  },
  leftPanel: {
    display: 'flex',
    // backgroundColor: 'blue',
    flexDirection: 'column',
    width: { md: pxTovW(800), lg: 'unset' },
  },
  secondaryHeadingBox: {
    mb: { xs: pxToRem(20), md: pxTovW(20) },
    pl: { xs: pxToRem(20), md: '0' },
  },

  userImage: {
    width: { xs: pxToRem(60), md: pxTovW(87) },
    height: { xs: pxToRem(60), md: pxTovW(87) },
  },

  questionBox: {
    height: '70vh',
    overflow: 'auto',
  },

  instructionsPanel: {
    width: { xs: '95%', md: pxTovW(814) },
    height: { md: '70vh' },
    maxHeight: { xs: pxToRem(420), md: pxTovW(666) },
    margin: 'auto',
    marginBottom: { xs: pxToRem(20), md: 'unset' },
    backgroundColor: 'red',
  },
};
interface ViewAssessmentProps {
  status: 'assigned' | 'ended';
}

export const ViewAssessment = (props: ViewAssessmentProps) => {
  const sectionTabsScrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({
    isPointerDown: false,
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    pointerId: -1,
  });
  const teacher_id = getLocalStorage('userId');
  const navigate = useNavigate();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const isOMREnabled = useFeatureEnabled(
    user_info?.schoolDetails[0]?.featuresPlanInfo,
    Feature.OMR_ENABLED
  );
  // const { homework_id } = useParams();
  const [assessmentContent, setAssessmentContent] = useState<
    AssessmentContent | undefined
  >();
  const { isOffline } = useDownloadContext();
  const { current_assessmemt_classStats, selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { clicker_assessment_info } = deserify(
    useAppSelector((state) => state.clickerAssessment)
  );
  // console.log('selected_hw_id', selected_hw_id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const {
    LmsHomeworkCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [quesImagesEnabled, setQuesImagesEnabled] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedSectionNo, setSelectedSectionNo] = useState<number>();

  const isMultiSubjectFormative =
    assessmentContent?.assessment?.taskType ===
    TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM;

  const assessmentWithSections =
    assessmentContent?.multiSubjectSections || undefined;

  const sectionTabs: MultiSubjectSection[] = assessmentWithSections || [];

  const allQuestionIds =
    assessmentContent?.assessmentContent
      .map((item) => item.questionId)
      .filter((id) => id !== undefined) || [];

  const taskQuestionInfoSubjectWise =
    sectionTabs
      .find((section) => section.sectionNo === selectedSectionNo)
      ?.multiSubjectSubSections.map(
        (subSection) => subSection.subSectionMetaData
      )
      .flat() || [];

  const sortedQuestionIds = (
    isMultiSubjectFormative && selectedSectionNo
      ? taskQuestionInfoSubjectWise
          .sort((a, b) => a.sequence - b.sequence)
          .map((val) => val.questionId)
          .filter((val) => val !== undefined)
      : allQuestionIds
  ).filter((val) => val !== undefined) as string[];

  const displayedQuestions = sortedQuestionIds
    .map((questionId) =>
      assessmentContent?.assessmentContent.find(
        (item) => item.questionId === questionId
      )
    )
    .filter(
      (
        item
      ): item is NonNullable<AssessmentContent['assessmentContent'][number]> =>
        item !== undefined
    );

  // ----------------------------------------------------

  const getHideBloomLevel = () => {
    if (assessmentContent?.multiSubjectSections?.length) {
      return (
        assessmentContent.multiSubjectSections[0]?.markingSchemeType ===
        MarkingSchemeEnum.MARKING_SCHEME_JEE
      );
    }

    return (
      assessmentContent?.assessment?.markingSchemeType ===
      MarkingSchemeEnum.MARKING_SCHEME_JEE
    );
  };

  // ----------------------------------------------------

  const getHomeworkDetails = async () => {
    try {
      setLoading(true);
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: selected_assessment_id,
          }
        );
      if (response.data) {
        await aiHomeworkOpenEvent({
          homeworkId: selected_assessment_id,
          isOffline: isOffline,
        });
        await aiHomeworkViewEvent({
          homeworkId: selected_assessment_id,
          isOffline: isOffline,
        });
        const hw = response.data;
        // const sortedQuestionIds = taskQuestionInfo
        //   .sort((a, b) => a.sequence - b.sequence)
        //   .map((val) => val.questionId)
        //   .filter((val) => val !== undefined) as string[];
        // const sortedHwArray = sortedQuestionIds
        //   .map((id) =>
        //     hw.assessmentContent.find((elem) => elem.questionId === id)
        //   )
        //   .filter((elem) => elem !== undefined) as Question[];
        const updatedHw = buildSortedHomeworkContent(hw);
        setAssessmentContent(updatedHw);
        const quesImagesEnabledCopy = { ...quesImagesEnabled };
        for (let i = 0; i < updatedHw.assessmentContent.length; i++) {
          if (questionHasImage(new Question(updatedHw.assessmentContent[i]))) {
            const questionId = updatedHw.assessmentContent[i]?.questionId;
            if (questionId) {
              quesImagesEnabledCopy[questionId] = true;
            }
          }
        }
        setQuesImagesEnabled(quesImagesEnabledCopy);
        setLoading(false);
        // console.log(response.data);
      }
    } catch (err) {
      setLoading(false);
      setError(true);
      console.log(err);
    }
  };
  useEffect(() => {
    if (selected_assessment_id && teacher_id) {
      getHomeworkDetails();
    }
  }, []);

  useEffect(() => {
    if (!isMultiSubjectFormative) {
      setSelectedSectionNo(undefined);
      return;
    }

    if (sectionTabs.length > 0 && selectedSectionNo === undefined) {
      setSelectedSectionNo(sectionTabs[0].sectionNo);
    }
  }, [isMultiSubjectFormative, sectionTabs, selectedSectionNo]);

  const handleSectionTabsPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = sectionTabsScrollRef.current;
    if (!container) return;

    dragStateRef.current = {
      isPointerDown: true,
      isDragging: false,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
      pointerId: event.pointerId,
    };
  };

  const handleSectionTabsPointerMove = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = sectionTabsScrollRef.current;
    if (!container) return;
    if (!dragStateRef.current.isPointerDown) return;
    if (dragStateRef.current.pointerId !== event.pointerId) return;

    if (event.pointerType === 'mouse' && event.buttons === 0) {
      dragStateRef.current.isPointerDown = false;
      dragStateRef.current.isDragging = false;
      dragStateRef.current.pointerId = -1;
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;

    if (!dragStateRef.current.isDragging) {
      if (Math.abs(deltaX) < 5) return;
      dragStateRef.current.isDragging = true;
      container.setPointerCapture(event.pointerId);
    }

    container.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
  };

  const handleSectionTabsPointerUp = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const container = sectionTabsScrollRef.current;
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  };

  const handleSectionTabsPointerLeave = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };

  const handleSectionTabsPointerCancel = () => {
    dragStateRef.current.isPointerDown = false;
    dragStateRef.current.isDragging = false;
    dragStateRef.current.pointerId = -1;
  };

  return loading === true ? (
    <Loader />
  ) : error === true ? (
    <Typography>Error Occured</Typography>
  ) : (
    <Box sx={styles.root}>
      <Typography variant="h1" fontWeight="bold">
        View Assessment
      </Typography>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel}>
          <ViewHwStatCard
            status={props.status}
            homework={assessmentContent?.assessment}
            multiSubjectSections={assessmentContent?.multiSubjectSections}
            attemptScoreInAssigned={current_assessmemt_classStats?.assessment?.classScore?.toFixed(
              0
            )}
            attemptNumberInAssigned={
              clicker_assessment_info?.selected_attempt?.attemptNumber
            }
            questionList={assessmentContent?.assessmentContent}
            quesImagesEnabled={quesImagesEnabled}
          />

          {assessmentContent?.assessment?.submissionType ===
            SubmissionType.SUBMISSION_OFFLINE && (
            <Box sx={styles.physicalSubmission}>
              <Typography variant="h2" fontWeight={600}>
                Physical Submission
              </Typography>
              <ImageWrapper
                name="physicalSubmission"
                type="png"
                styles={{
                  width: { xs: pxToRem(219), lg: pxTovW(313) },
                  height: { xs: pxToRem(153), lg: pxTovW(241) },
                  alignSelf: 'center',
                }}
              />
              {/* <Typography
                variant="h3"
                sx={{
                  width: { xs: pxToRem(219), lg: pxTovW(313) },
                  alignSelf: 'center',
                }}
              ></Typography> */}
            </Box>
          )}

          {isOMREnabled && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={() =>
                  navigate(
                    `${OMR_ACTIVE}/${assessmentContent?.assessment?.assessmentId}`
                  )
                }
              >
                OMR Responses
              </Button>
            </Box>
          )}
        </Box>

        {assessmentContent?.assessment?.taskType !==
        TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM ? (
          <Box sx={styles.rightPanel}>
            <Box sx={styles.secondaryHeadingBox}>
              <Typography variant="h2" fontWeight="bold">
                Assessment Questions
              </Typography>
            </Box>
            {isMultiSubjectFormative && sectionTabs.length > 0 ? (
              <Box
                ref={sectionTabsScrollRef}
                onPointerDown={handleSectionTabsPointerDown}
                onPointerMove={handleSectionTabsPointerMove}
                onPointerUp={handleSectionTabsPointerUp}
                onPointerLeave={handleSectionTabsPointerLeave}
                onPointerCancel={handleSectionTabsPointerCancel}
                sx={{
                  display: 'block',
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  cursor: 'grab',
                  touchAction: 'pan-x',
                  userSelect: 'none',
                  px: { xs: pxToRem(16), md: pxTovW(24) },
                  py: { xs: pxToRem(4), md: pxTovW(6) },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  mb: { xs: pxToRem(20), md: pxTovW(20) },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    width: 'max-content',
                    minWidth: '100%',
                    gap: { xs: pxToRem(8), md: pxTovW(20) },
                    px: { xs: pxToRem(6), md: pxTovW(12) },
                    py: { xs: pxToRem(10), md: pxTovW(10) },
                    backgroundColor: '#F3F9FE',
                    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                    boxSizing: 'border-box',
                    '&::before, &::after': {
                      content: '""',
                      width: { xs: pxToRem(6), md: pxTovW(10) },
                      flex: '0 0 auto',
                    },
                  }}
                >
                  {sectionTabs.map((section) => {
                    const sectionTitle =
                      (section as { sectionTitle?: string }).sectionTitle ||
                      section.sectionName;
                    const subject = `${getSubjectNameFromEnum(
                      section.subject
                    )}${sectionTitle ? ` - ${sectionTitle}` : ''}`;
                    const isSelected = section.sectionNo === selectedSectionNo;

                    return (
                      <Box
                        key={`${section.subject}-${section.sectionNo}`}
                        onClick={() => setSelectedSectionNo(section.sectionNo)}
                        sx={{
                          flex: '0 0 auto',
                          scrollSnapAlign: 'start',
                          height: { xs: pxToRem(32), md: pxTovW(50) },
                          minWidth: { xs: pxToRem(100), md: pxTovW(150) },
                          px: { xs: pxToRem(12), md: pxTovW(14) },
                          borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          backgroundColor: isSelected
                            ? '#007CDC'
                            : 'transparent',
                          color: isSelected ? '#fff' : '#000',
                          '&:hover': {
                            backgroundColor: isSelected ? '#007CDC' : '#E3F2FD',
                          },
                        }}
                      >
                        <Typography
                          variant="h4"
                          fontWeight={isSelected ? 'bold' : 500}
                          color="inherit"
                          noWrap
                        >
                          {subject}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            {displayedQuestions.map((que, quePos) => (
              <Box
                key={que.questionId || quePos}
                sx={{
                  marginBottom: { xs: pxToRem(20), md: pxTovW(20) },
                  px: { xs: pxToRem(16), md: 0 },
                }}
              >
                <QuestionContainerWithSolution
                  questionNumber={quePos + 1}
                  question={que}
                  showAnswer={true}
                  showQuestionStats
                  disableInput={true}
                  imageResizeEnabled={true}
                  headerRightElement={
                    questionHasImage(que) &&
                    assessmentContent?.assessment?.assessmentId &&
                    assessmentContent?.assessment?.taskType !==
                      TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM ? (
                      <Box sx={{ ml: 'auto' }}>
                        <IncludeImageButton
                          isChecked={
                            !!(
                              que.questionId &&
                              quesImagesEnabled[que.questionId]
                            )
                          }
                          onClick={() =>
                            que.questionId &&
                            setQuesImagesEnabled({
                              ...quesImagesEnabled,
                              [que.questionId]:
                                !quesImagesEnabled[que.questionId],
                            })
                          }
                        />
                      </Box>
                    ) : undefined
                  }
                  // hideBloomLevel={
                  //   assessmentContent?.assessment?.markingSchemeType ===
                  //   MarkingSchemeEnum.MARKING_SCHEME_JEE
                  //     ? true
                  //     : false
                  // }
                  hideBloomLevel={getHideBloomLevel()}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: { xs: '95%', md: pxTovW(950), lg: pxTovW(814) },
            }}
          >
            <InstructionsPanel
              customSx={{
                // backgroundColor: 'red',
                display: 'flex',
                flexGrow: 1,
                width: '100%',
              }}
              Katexvalue={
                assessmentContent.assessment.assessmentInstructions[0]
              }
              AttachmentList={
                assessmentContent.assessment.taskUploads.filter(
                  (e) => e.isDeleted === false
                ) || undefined
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
